/**
 * @fileoverview UnidadRepository - Repository for unidad collection
 */

import { DataRepository } from './base/DataRepository';
import type { 
  Unidad, 
  CreateUnidadData, 
  UpdateUnidadData 
} from '../types/collections/Unidad';
import type { QueryOptions, PaginationResult } from '../types/common/PocketBaseTypes';

/**
 * Repository for managing Unidad records
 * Extends DataRepository with Unidad-specific methods
 */
export class UnidadRepository extends DataRepository<Unidad> {
  constructor() {
    super('unidad');
  }

  /**
   * Get active unidades with pagination
   * @param page Page number (1-based)
   * @param pageSize Number of records per page
   * @param options Additional query options
   * @returns Paginated result of active unidades
   */
  async getActiveUnidades(
    page: number = 1, 
    pageSize: number = 10, 
    options: QueryOptions = {}
  ): Promise<PaginationResult<Unidad>> {
    const queryOptions: QueryOptions = {
      filter: 'activo = true',
      sort: 'nombre',
      ...options,
    };

    return this.getList(page, pageSize, queryOptions);
  }

  /**
   * Get inactive unidades with pagination
   * @param page Page number (1-based)
   * @param pageSize Number of records per page
   * @param options Additional query options
   * @returns Paginated result of inactive unidades
   */
  async getInactiveUnidades(
    page: number = 1, 
    pageSize: number = 10, 
    options: QueryOptions = {}
  ): Promise<PaginationResult<Unidad>> {
    const queryOptions: QueryOptions = {
      filter: 'activo = false',
      sort: 'nombre',
      ...options,
    };

    return this.getList(page, pageSize, queryOptions);
  }

  /**
   * Get all active unidades without pagination
   * @param options Additional query options
   * @returns Array of active unidades
   */
  async getAllActive(options: QueryOptions = {}): Promise<Unidad[]> {
    const queryOptions: QueryOptions = {
      filter: 'activo = true',
      sort: 'nombre',
      ...options,
    };

    return this.getAll(queryOptions);
  }

  /**
   * Find unidad by name
   * @param nombre Unit name to search for
   * @param options Additional query options
   * @returns Unidad record or null if not found
   */
  async findByName(nombre: string, options: QueryOptions = {}): Promise<Unidad | null> {
    const filter = `nombre = "${nombre}"`;
    return this.findFirst(filter, options);
  }

  /**
   * Create new unidad with validation
   * @param data Unidad data to create
   * @returns Created unidad record
   */
  async createUnidad(data: CreateUnidadData): Promise<Unidad> {
    // Validate required fields
    if (!data.nombre || data.nombre.trim() === '') {
      throw new Error('Nombre is required');
    }

    // Set default values and convert nombre to uppercase
    const createData: CreateUnidadData = {
      activo: true, // Default to active
      ...data,
      nombre: data.nombre.trim().toUpperCase(), // Always store nombre in uppercase
    };

    return this.create(createData);
  }

  /**
   * Update existing unidad with validation
   * @param id Unidad ID to update
   * @param data Data to update
   * @returns Updated unidad record
   */
  async updateUnidad(id: string, data: UpdateUnidadData): Promise<Unidad> {
    // Validate nombre if provided
    if (data.nombre !== undefined && (!data.nombre || data.nombre.trim() === '')) {
      throw new Error('Nombre cannot be empty');
    }

    // Convert nombre to uppercase if provided
    const updateData: UpdateUnidadData = {
      ...data,
      ...(data.nombre && { nombre: data.nombre.trim().toUpperCase() }),
    };

    return this.update(id, updateData);
  }

  /**
   * Soft delete unidad (set activo = false)
   * @param id Unidad ID to deactivate
   * @returns Updated unidad record
   */
  async deactivate(id: string): Promise<Unidad> {
    return this.update(id, { activo: false });
  }

  /**
   * Restore unidad (set activo = true)
   * @param id Unidad ID to activate
   * @returns Updated unidad record
   */
  async activate(id: string): Promise<Unidad> {
    return this.update(id, { activo: true });
  }

  /**
   * Check if unidad name already exists
   * @param nombre Unit name to check
   * @param excludeId Optional ID to exclude from check (for updates)
   * @returns True if name exists, false otherwise
   */
  async nameExists(nombre: string, excludeId?: string): Promise<boolean> {
    const filter = excludeId 
      ? `nombre = "${nombre}" && id != "${excludeId}"`
      : `nombre = "${nombre}"`;
    
    const existing = await this.findFirst(filter);
    return existing !== null;
  }

  /**
   * Get unidad statistics
   * @returns Object with total, active, and inactive counts
   */
  async getStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> {
    const [total, active, inactive] = await Promise.all([
      this.count(),
      this.count('activo = true'),
      this.count('activo = false'),
    ]);

    return { total, active, inactive };
  }
}
